import { StrictMode } from 'react'
import './i18n'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import seoContent from '@/content/seo.json'
import siteContent from '@/content/site.json'

const setMeta = (selector: string, value: string) => {
  const element = document.head.querySelector<HTMLMetaElement>(selector);
  if (element) element.content = value;
};

const upsertMeta = (selector: string, attrs: Record<string, string>, value: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    Object.entries(attrs).forEach(([key, attrValue]) => element?.setAttribute(key, attrValue));
    document.head.appendChild(element);
  }
  element.content = value;
};

const upsertJsonLd = (id: string, data: unknown) => {
  let element = document.getElementById(id) as HTMLScriptElement | null;
  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.id = id;
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
};

document.title = seoContent.title;
setMeta('meta[name="description"]', seoContent.description);
setMeta('meta[name="keywords"]', seoContent.keywords);
document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', seoContent.canonicalUrl);
setMeta('meta[name="Last-modified"]', seoContent.lastModified);
upsertMeta('meta[name="geo.region"]', { name: 'geo.region' }, seoContent.geo.region);
upsertMeta('meta[name="geo.placename"]', { name: 'geo.placename' }, seoContent.geo.placename);
upsertMeta('meta[name="geo.position"]', { name: 'geo.position' }, `${seoContent.geo.latitude};${seoContent.geo.longitude}`);
upsertMeta('meta[name="ICBM"]', { name: 'ICBM' }, `${seoContent.geo.latitude}, ${seoContent.geo.longitude}`);
setMeta('meta[property="og:title"]', seoContent.openGraph.title);
setMeta('meta[property="og:description"]', seoContent.openGraph.description);
setMeta('meta[property="og:url"]', seoContent.canonicalUrl);
setMeta('meta[property="og:site_name"]', siteContent.name);
setMeta('meta[property="og:image"]', seoContent.openGraph.image);
setMeta('meta[name="twitter:title"]', seoContent.twitter.title);
setMeta('meta[name="twitter:description"]', seoContent.twitter.description);
setMeta('meta[name="twitter:image"]', seoContent.twitter.image);

upsertJsonLd('cms-local-business-schema', {
  '@context': 'https://schema.org',
  '@type': seoContent.schema.businessType,
  name: seoContent.schema.businessName,
  url: siteContent.websiteUrl,
  telephone: seoContent.schema.telephone,
  email: seoContent.schema.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: seoContent.schema.streetAddress,
    addressLocality: seoContent.schema.addressLocality,
    addressRegion: seoContent.schema.addressRegion,
    postalCode: seoContent.schema.postalCode,
    addressCountry: seoContent.schema.addressCountry,
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: seoContent.geo.latitude,
    longitude: seoContent.geo.longitude,
  },
  areaServed: seoContent.schema.areaServed,
  priceRange: seoContent.schema.priceRange,
  sameAs: seoContent.schema.sameAs,
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
